// src/Pages/Support/KYC/KycQueue.jsx
import React, { useEffect } from "react";
import {
  Box,
  Grid,
  Card,
  CardMedia,
  CardContent,
  Typography,
  Button,
  Stack,
  Alert,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchPendingKyc,
  approveKyc,
  rejectKyc,
  selectPendingKyc,
  selectKycLoading,
  selectKycError,
} from "../../../store/Slices/kycSlice";

// هذا الأساس الذي نستعمله لبناء روابط الصور و روابط التحميل
const API_ORIGIN = process.env.REACT_APP_API_ORIGIN || "http://localhost:4000";

export default function KycQueue() {
  const dispatch = useDispatch();

  // بيانات من الـ Redux
  const pending = useSelector(selectPendingKyc);
  const loading = useSelector(selectKycLoading);
  const error = useSelector(selectKycError);

  // عند فتح الصفحة أول مرة، نجلب الطلبات
  useEffect(() => {
    dispatch(fetchPendingKyc());
  }, [dispatch]);

  /**
   * 🖼 imgUrl:
   * السيرفر الآن يخزن المسار كشيء مثل:
   *   "uploads/kyc/<userId>/front.png"
   * وفي بعض الطلبات القديمة قد يكون المسار كامل من الويندوز:
   *   "E:\\...\\uploads\\kyc\\<userId>\\front.png"
   *
   * هدفنا: إرجاع رابط يمكن فتحه في المتصفح مثل:
   *   http://localhost:4000/uploads/kyc/<userId>/front.png
   */
  const imgUrl = (storedPath) => {
    if (!storedPath) return "";

    // حوّل الباك سلاشز \ إلى سلاش /
    const normalized = String(storedPath).replace(/\\/g, "/");

    // لو الـ path فيه "uploads/..." داخله (حتى لو كان مسار مطلق من الويندوز)
    const match = normalized.match(/uploads\/.+/);
    const relative = match ? match[0] : normalized;

    // ابنِ URL النهائي
    return `${API_ORIGIN}/${relative.replace(/^\/+/, "")}`;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        KYC Pending Requests
      </Typography>

      {/* حالة التحميل */}
      {loading && (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading pending KYC requests...
          </Typography>
        </Stack>
      )}

      {/* حالة الخطأ (مثل Forbidden أو أي خطأ من الـ API) */}
      {!loading && error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {String(error)}
          {String(error).toLowerCase() === "forbidden" && (
            <>
              {" "}
              — You are not allowed to view KYC queue with this account. Please
              use a support/admin account.
            </>
          )}
        </Alert>
      )}

      {/* حالة عدم وجود طلبات */}
      {!loading && !error && (!pending || pending.length === 0) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          No pending KYC requests found.
        </Alert>
      )}

      <Grid container spacing={2}>
        {(pending || []).map((req) => {
          const frontSrc = imgUrl(req.front_image_path);
          const backSrc = imgUrl(req.back_image_path);

          return (
            <Grid key={req._id} item xs={12} md={6} lg={4}>
              <Card>
                <CardContent>
                  {/* بيانات المستخدم و حالة الطلب */}
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    sx={{ mb: 1 }}
                  >
                    <Typography
                      variant="subtitle1"
                      noWrap
                      title={req.user_id?.email}
                    >
                      {req.user_id?.username || "User"} — {req.user_id?.email}
                    </Typography>

                    <Chip
                      size="small"
                      label={String(req.status || "pending").toLowerCase()}
                      color={
                        String(req.status || "pending").toLowerCase() ===
                        "pending"
                          ? "warning"
                          : String(req.status || "").toLowerCase() ===
                            "approved"
                          ? "success"
                          : "error"
                      }
                    />
                  </Stack>

                  {/* وقت الإنشاء */}
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.secondary",
                      mb: 1,
                      display: "block",
                    }}
                  >
                    Created:{" "}
                    {req.created_at
                      ? new Date(req.created_at).toLocaleString()
                      : "N/A"}
                  </Typography>

                  {/* صور الهوية */}
                  <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <CardMedia
                      component="img"
                      sx={{
                        width: "50%",
                        maxHeight: 200,
                        border: "1px solid #444",
                        bgcolor: "#000",
                        objectFit: "contain",
                      }}
                      image={frontSrc}
                      alt="Front ID"
                    />

                    <CardMedia
                      component="img"
                      sx={{
                        width: "50%",
                        maxHeight: 200,
                        border: "1px solid #444",
                        bgcolor: "#000",
                        objectFit: "contain",
                      }}
                      image={backSrc}
                      alt="Back ID"
                    />
                  </Stack>

                  {/* أزرار تحميل الصورة بالدقة الأصلية */}
                  <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <Button
                      variant="outlined"
                      size="small"
                      component="a"
                      href={frontSrc}
                      download
                    >
                      Download Front
                    </Button>

                    <Button
                      variant="outlined"
                      size="small"
                      component="a"
                      href={backSrc}
                      download
                    >
                      Download Back
                    </Button>
                  </Stack>

                  {/* أزرار الموافقة / الرفض */}
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => dispatch(approveKyc(req._id))}
                      disabled={loading}
                    >
                      Approve
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() =>
                        dispatch(
                          rejectKyc({ id: req._id, reason: "Not clear" })
                        )
                      }
                      disabled={loading}
                    >
                      Reject
                    </Button>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
}
